import { gql, useMutation, useQuery } from '@apollo/client';
import {
  CvlFinancialQualificationCriteriaEnumType,
  InvestorAccreditationApplicationFinancialType,
} from '__generated__/graphql';
import { FC, useEffect, useState } from 'react';

import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

import { formatCurrencyToShort } from 'utils/format-helper';

// import { IndividualCriteria } from '../../Accreditation/EligibilityCriteria';

const FinancialQualificationCriteriaHelperText: Record<
  CvlFinancialQualificationCriteriaEnumType,
  string
> = {
  [CvlFinancialQualificationCriteriaEnumType.AnnualIncome]:
    'Annual income of at least INR 2 Crores',
  [CvlFinancialQualificationCriteriaEnumType.NetWorth]:
    'Net worth of at least INR 7.5 Crores (Net Worth should INCLUDE Financial Assets >= INR 3.75 Crore)',
  [CvlFinancialQualificationCriteriaEnumType.Combined]:
    'Annual income of at least INR 1 Crore and net worth of at least INR 5 Crores (including >= INR 2.5 Crore Financial Assets)',
};

// Thresholds per qualification criteria, mirroring EligibilityCriteria.ts /
// HelperComponents.tsx and the helper text above.
const CRORE = 10000000;

const CRITERIA_MIN_VALUES: Record<
  CvlFinancialQualificationCriteriaEnumType,
  { annualIncome?: number; netWorth?: number; financialAssets?: number }
> = {
  [CvlFinancialQualificationCriteriaEnumType.AnnualIncome]: {
    annualIncome: 2 * CRORE,
  },
  [CvlFinancialQualificationCriteriaEnumType.NetWorth]: {
    netWorth: 7.5 * CRORE,
    financialAssets: 3.75 * CRORE,
  },
  [CvlFinancialQualificationCriteriaEnumType.Combined]: {
    annualIncome: 1 * CRORE,
    netWorth: 5 * CRORE,
    financialAssets: 2.5 * CRORE,
  },
};

type Props = {
  selectedInvestmentEntity: any | null;
  onSuccess?: (data: {
    qualificationCriteria: CvlFinancialQualificationCriteriaEnumType;
    grossAnnualIncomeFY2526?: number;
    netWorth2526?: number;
    financialAssetFY2526?: number;
    grossAnnualIncomeFY2425?: number;
    netWorth2425?: number;
    financialAssetFY2425?: number;
  }) => void;
  onBack?: () => void;
  locked?: boolean;
};

const CVL_INVESTOR_APPLICATION_ID_QUERY = gql(`
  query InvestorAccreditationApplicationIdForFinancialDetails($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      validityPeriodOfAccreditation
      financialDetails {
        annualIncomeYear1
        annualIncomeYear2
        financialAssetsYear1
        financialAssetsYear2
        netWorthYear1
        netWorthYear2
        qualificationCriteria
      }
    }
  }
`);

export const ADD_FINANCIAL_DETAILS_MUTATION = gql(`
  mutation AddFinancialDetails(
    $investorAccreditationApplicationId: ID!
    $qualificationCriteria: CvlFinancialQualificationCriteriaEnumType!
    $annualIncomeYear1: Float
    $annualIncomeYear2: Float
    $financialAssetsYear1: Float
    $financialAssetsYear2: Float
    $netWorthYear1: Float
    $netWorthYear2: Float
  ) {
    addFinancialDetails(
      investorAccreditationApplicationId: $investorAccreditationApplicationId
      qualificationCriteria: $qualificationCriteria
      annualIncomeYear1: $annualIncomeYear1
      annualIncomeYear2: $annualIncomeYear2
      financialAssetsYear1: $financialAssetsYear1
      financialAssetsYear2: $financialAssetsYear2
      netWorthYear1: $netWorthYear1
      netWorthYear2: $netWorthYear2
    ) {
      id
    }
  }
`);

const FinancialDetails: FC<Props> = ({ selectedInvestmentEntity, onSuccess, onBack, locked }) => {
  const [selectedCriteria, setSelectedCriteria] = useState<
    CvlFinancialQualificationCriteriaEnumType | ''
  >('');
  // Tracked so the Financial Asset field can be capped at the entered Net
  // Worth (net worth must include, i.e. be >= financial assets).
  const [netWorthValue, setNetWorthValue] = useState<number | undefined>(undefined);
  const [netWorthValueYear2, setNetWorthValueYear2] = useState<number | undefined>(undefined);

  const { data: cvlData } = useQuery(CVL_INVESTOR_APPLICATION_ID_QUERY, {
    variables: { investmentEntityId: selectedInvestmentEntity?.id },
    skip: !selectedInvestmentEntity?.id,
    fetchPolicy: 'network-only',
  });

  const financialDetails = cvlData?.investorAccreditationApplication?.financialDetails as
    | InvestorAccreditationApplicationFinancialType
    | null
    | undefined;

  const existingQualificationCriteria = financialDetails?.qualificationCriteria as
    | CvlFinancialQualificationCriteriaEnumType
    | undefined;

  const mappedExistingQualificationCriteria =
    (existingQualificationCriteria as unknown as CvlFinancialQualificationCriteriaEnumType) ?? '';

  useEffect(() => {
    if (mappedExistingQualificationCriteria && !selectedCriteria) {
      setSelectedCriteria(mappedExistingQualificationCriteria);
    }
  }, [mappedExistingQualificationCriteria, selectedCriteria]);

  const rawValidity = cvlData?.investorAccreditationApplication?.validityPeriodOfAccreditation;
  const validityCode =
    typeof rawValidity === 'number'
      ? rawValidity
      : rawValidity != null
      ? Number(rawValidity)
      : undefined;
  // Backend requires two years of financial data (FY 25-26 and FY 24-25)
  // when the investor has opted for the 3-year accreditation validity.
  const isThreeYearValidity = validityCode === 2;

  const defaultAnnualIncome = financialDetails?.annualIncomeYear1 ?? undefined;
  const defaultNetWorth = financialDetails?.netWorthYear1 ?? undefined;
  const defaultFinancialAssets = financialDetails?.financialAssetsYear1 ?? undefined;

  const defaultAnnualIncomeYear2 = financialDetails?.annualIncomeYear2 ?? undefined;
  const defaultNetWorthYear2 = financialDetails?.netWorthYear2 ?? undefined;
  const defaultFinancialAssetsYear2 = financialDetails?.financialAssetsYear2 ?? undefined;

  useEffect(() => {
    if (defaultNetWorth != null && netWorthValue === undefined) {
      setNetWorthValue(defaultNetWorth);
    }
  }, [defaultNetWorth, netWorthValue]);

  useEffect(() => {
    if (defaultNetWorthYear2 != null && netWorthValueYear2 === undefined) {
      setNetWorthValueYear2(defaultNetWorthYear2);
    }
  }, [defaultNetWorthYear2, netWorthValueYear2]);

  const [addFinancialDetails, { loading, error }] = useMutation(ADD_FINANCIAL_DETAILS_MUTATION);

  const handleSubmit = (data: any) => {
    const cvlApplicationId = cvlData?.investorAccreditationApplication?.id;

    if (!selectedInvestmentEntity?.id || !cvlApplicationId) {
      // eslint-disable-next-line no-console
      console.error(
        'Missing investment entity or CVL investor application ID when adding financial details'
      );
      return;
    }

    if (!data.qualificationCriteria) {
      return;
    }

    return addFinancialDetails({
      variables: {
        investorAccreditationApplicationId: cvlApplicationId,
        qualificationCriteria:
          data.qualificationCriteria as unknown as CvlFinancialQualificationCriteriaEnumType,
        annualIncomeYear1: data.grossAnnualIncomeFY2526 ?? undefined,
        annualIncomeYear2: isThreeYearValidity
          ? data.grossAnnualIncomeFY2425 ?? undefined
          : undefined,
        financialAssetsYear1: data.financialAssetFY2526 ?? undefined,
        financialAssetsYear2: isThreeYearValidity
          ? data.financialAssetFY2425 ?? undefined
          : undefined,
        netWorthYear1: data.netWorth2526 ?? undefined,
        netWorthYear2: isThreeYearValidity ? data.netWorth2425 ?? undefined : undefined,
      },
    }).then(() => {
      if (onSuccess) {
        onSuccess({
          qualificationCriteria: data.qualificationCriteria,
          ...(data.grossAnnualIncomeFY2526 != null && {
            grossAnnualIncomeFY2526: data.grossAnnualIncomeFY2526,
          }),
          ...(data.netWorth2526 != null && { netWorth2526: data.netWorth2526 }),
          ...(data.financialAssetFY2526 != null && {
            financialAssetFY2526: data.financialAssetFY2526,
          }),
          ...(isThreeYearValidity &&
            data.grossAnnualIncomeFY2425 != null && {
              grossAnnualIncomeFY2425: data.grossAnnualIncomeFY2425,
            }),
          ...(isThreeYearValidity &&
            data.netWorth2425 != null && { netWorth2425: data.netWorth2425 }),
          ...(isThreeYearValidity &&
            data.financialAssetFY2425 != null && {
              financialAssetFY2425: data.financialAssetFY2425,
            }),
        });
      }
    });
  };

  return (
    <div>
      <p className="mb-4 text-sm text-gray-600">
        Select the criteria under which you qualify for accreditation.
      </p>
      {locked ? (
        <FormPanelWithReadMode
          key={`${selectedInvestmentEntity?.id ?? 'none'}-${
            existingQualificationCriteria ?? 'none'
          }`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitButtonLabel="Next"
          disableEdit={true}
        >
          <FormInput
            fieldName="qualificationCriteria"
            label="Qualification Criteria"
            type="select"
            placeholder="Select the criteria under which you qualify"
            defaultValue={mappedExistingQualificationCriteria}
            options={[
              {
                value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                label: 'Annual Income',
              },
              { value: CvlFinancialQualificationCriteriaEnumType.NetWorth, label: 'Net Worth' },
              {
                value: CvlFinancialQualificationCriteriaEnumType.Combined,
                label: 'Combined - Net Worth & Income',
              },
            ]}
            validators={{ required: true }}
            onChange={data => setSelectedCriteria(data.qualificationCriteria)}
          />
          <div
            className={
              selectedCriteria
                ? 'col-span-full mt-1 mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 border border-amber-200'
                : 'hidden'
            }
          >
            Requirements for the selected criteria:{' '}
            {selectedCriteria && FinancialQualificationCriteriaHelperText[selectedCriteria]}
          </div>
          <FormInput
            fieldName="grossAnnualIncomeFY2526"
            label="Gross Annual Income for FY 25-26"
            type="number"
            defaultValue={defaultAnnualIncome}
            placeholder="Enter gross annual income"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                : undefined,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorth2526"
            label="Net Worth 25-26"
            type="number"
            defaultValue={defaultNetWorth}
            placeholder="Enter net worth"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                : undefined,
            }}
            onChange={data => setNetWorthValue(data.netWorth2526)}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="financialAssetFY2526"
            label="Financial Asset FY 25-26"
            type="number"
            defaultValue={defaultFinancialAssets}
            placeholder="Enter financial asset"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                : undefined,
              maxValue: netWorthValue,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <div
            className={
              isThreeYearValidity
                ? 'col-span-full mt-2 mb-1 text-sm font-medium text-indigo-700'
                : 'hidden'
            }
          >
            A 3-year validity requires financial details for one additional year: FY 24-25.
          </div>
          <FormInput
            fieldName="grossAnnualIncomeFY2425"
            label="Gross Annual Income for FY 24-25"
            type="number"
            defaultValue={defaultAnnualIncomeYear2}
            placeholder="Enter gross annual income"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                : undefined,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorth2425"
            label="Net Worth 24-25"
            type="number"
            defaultValue={defaultNetWorthYear2}
            placeholder="Enter net worth"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                : undefined,
            }}
            onChange={data => setNetWorthValueYear2(data.netWorth2425)}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="financialAssetFY2425"
            label="Financial Asset FY 24-25"
            type="number"
            defaultValue={defaultFinancialAssetsYear2}
            placeholder="Enter financial asset"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                : undefined,
              maxValue: netWorthValueYear2,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
        </FormPanelWithReadMode>
      ) : (
        <FormPanel
          key={`${selectedInvestmentEntity?.id ?? 'none'}-${
            existingQualificationCriteria ?? 'none'
          }`}
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitButtonLabel="Next"
          onCancel={onBack}
          cancelButtonLabel="Previous step"
          cancelButtonFirst
        >
          <FormInput
            fieldName="qualificationCriteria"
            label="Qualification Criteria"
            type="select"
            placeholder="Select the criteria under which you qualify"
            defaultValue={mappedExistingQualificationCriteria}
            options={[
              {
                value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                label: 'Annual Income',
              },
              { value: CvlFinancialQualificationCriteriaEnumType.NetWorth, label: 'Net Worth' },
              {
                value: CvlFinancialQualificationCriteriaEnumType.Combined,
                label: 'Combined - Net Worth & Income',
              },
            ]}
            validators={{ required: true }}
            onChange={data => setSelectedCriteria(data.qualificationCriteria)}
          />
          <div
            className={
              selectedCriteria
                ? 'col-span-full mt-1 mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 border border-amber-200'
                : 'hidden'
            }
          >
            Requirements for the selected criteria:{' '}
            {selectedCriteria && FinancialQualificationCriteriaHelperText[selectedCriteria]}
          </div>
          <FormInput
            fieldName="grossAnnualIncomeFY2526"
            label="Gross Annual Income for FY 25-26"
            type="number"
            defaultValue={defaultAnnualIncome}
            placeholder="Enter gross annual income"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                : undefined,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />

          <FormInput
            fieldName="netWorth2526"
            label="Net Worth 25-26"
            type="number"
            defaultValue={defaultNetWorth}
            placeholder="Enter net worth"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                : undefined,
            }}
            onChange={data => setNetWorthValue(data.netWorth2526)}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="financialAssetFY2526"
            label="Financial Asset FY 25-26"
            type="number"
            defaultValue={defaultFinancialAssets}
            placeholder="Enter financial asset"
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                : undefined,
              maxValue: netWorthValue,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <div
            className={
              isThreeYearValidity
                ? 'col-span-full mt-2 mb-1 text-sm font-medium text-indigo-700'
                : 'hidden'
            }
          >
            A 3-year validity requires financial details for one additional year: FY 24-25.
          </div>
          <FormInput
            fieldName="grossAnnualIncomeFY2425"
            label="Gross Annual Income for FY 24-25"
            type="number"
            defaultValue={defaultAnnualIncomeYear2}
            placeholder="Enter gross annual income"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].annualIncome
                : undefined,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.AnnualIncome,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="netWorth2425"
            label="Net Worth 24-25"
            type="number"
            defaultValue={defaultNetWorthYear2}
            placeholder="Enter net worth"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].netWorth
                : undefined,
            }}
            onChange={data => setNetWorthValueYear2(data.netWorth2425)}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
          <FormInput
            fieldName="financialAssetFY2425"
            label="Financial Asset FY 24-25"
            type="number"
            defaultValue={defaultFinancialAssetsYear2}
            placeholder="Enter financial asset"
            hidden={!isThreeYearValidity}
            helperText={
              selectedCriteria
                ? `Minimum ${formatCurrencyToShort(
                    CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                  )} required`
                : undefined
            }
            validators={{
              required: true,
              minValue: selectedCriteria
                ? CRITERIA_MIN_VALUES[selectedCriteria].financialAssets
                : undefined,
              maxValue: netWorthValueYear2,
            }}
            conditionsToShow={{
              type: 'some',
              matches: [
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.NetWorth,
                },
                {
                  field: 'qualificationCriteria',
                  condition: '===',
                  value: CvlFinancialQualificationCriteriaEnumType.Combined,
                },
              ],
            }}
          />
        </FormPanel>
      )}
    </div>
  );
};

export default FinancialDetails;
